'use client';
import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { createContato } from '@/services/contato/createContato';
import { IPAGEContato } from '@/services/types/SingleTypes';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';
import { getHighlightPositions } from '@/lib/utils';

// Dynamically import Leaflet only on client side
const LeafletMap = dynamic(() => import('./LeafletMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <div className="text-gray-600">Carregando mapa...</div>
    </div>
  ),
});

interface ContactFormData {
  name: string;
  apelido: string;
  email: string;
  telefone: string;
  assunto: string;
  mensagem: string;
}

interface ContactFormProps {
  contatoData?: IPAGEContato | null;
}

const ContactForm: React.FC<ContactFormProps> = ({ contatoData }) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    apelido: '',
    email: '',
    telefone: '',
    assunto: '',
    mensagem: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Limpar mensagens de erro quando o usuário começar a digitar
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
      setErrorMessage('');
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset status anterior
    setSubmitStatus('idle');
    setErrorMessage('');

    // Validação básica
    if (!formData.name || !formData.apelido || !formData.email || !formData.assunto || !formData.mensagem) {
      setSubmitStatus('error');
      setErrorMessage('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsSubmitting(true);

    try {
      await createContato(formData);

      // Sucesso
      setSubmitStatus('success');

      // Reset do formulário após sucesso
      setFormData({
        name: '',
        apelido: '',
        email: '',
        telefone: '',
        assunto: '',
        mensagem: ''
      });

      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);

    } catch (error) {
      console.error('Erro ao enviar contato:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full relative bg-white mb-0">
      <div className="flex flex-col lg:flex-row  relative">


        {/* Left Side - Formulário (60% em desktop) */}
        <form onSubmit={handleSubmit} className="w-full lg:w-3/5 px-4 sm:px-6 py-8 md:py-12 lg:px-12 lg:py-16 flex flex-col overflow-y-auto  order-1">

          <div className="ml-0 lg:ml-8 mt-16 lg:absolute  lg:left-1/8 lg:transform lg:-translate-x-1/8 lg:-translate-y-1/2 flex flex-col   gap-4 sm:gap-8 z-20">
            {contatoData?.endereco && (
              <a href={`mailto:${contatoData.email}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-900 transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M21.8178 7.68475C22.5632 8.25194 23 9.13431 23 10.071V20C23 21.6569 21.6569 23 20 23H4C2.34315 23 1 21.6569 1 20V10.071C1 9.13431 1.43675 8.25194 2.18224 7.68475C4.36739 6.02221 8.93135 2.55149 10.2 1.6C11.2667 0.8 12.7333 0.8 13.8 1.6C15.0686 2.55148 19.6326 6.02221 21.8178 7.68475ZM3 10.1688L10.1411 15.8065C11.231 16.667 12.769 16.667 13.8589 15.8065L21 10.1688V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V10.1688ZM12.6 3.2L19.7792 8.58443L12.6196 14.2367C12.2563 14.5236 11.7437 14.5236 11.3804 14.2367L4.22077 8.58443L11.4 3.2C11.7556 2.93333 12.2444 2.93333 12.6 3.2Z" fill="#F7941D" />
                </svg>

                <span className="text-sm font-normal ">{contatoData.email}</span>
              </a>
            )}
            {contatoData?.telefone && (
              <a href={`tel:${contatoData.telefone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-900 transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 5.5C3 14.0604 9.93959 21 18.5 21C18.8862 21 19.2691 20.9859 19.6483 20.9581C20.0834 20.9262 20.3009 20.9103 20.499 20.7963C20.663 20.7019 20.8185 20.5345 20.9007 20.364C21 20.1582 21 19.9181 21 19.438V16.6207C21 16.2169 21 16.015 20.9335 15.842C20.8749 15.6891 20.7795 15.553 20.6559 15.4456C20.516 15.324 20.3262 15.255 19.9468 15.117L16.74 13.9509C16.2985 13.7904 16.0777 13.7101 15.8683 13.7237C15.6836 13.7357 15.5059 13.7988 15.3549 13.9058C15.1837 14.0271 15.0629 14.2285 14.8212 14.6314L14 16C11.3501 14.7999 9.2019 12.6489 8 10L9.36863 9.17882C9.77145 8.93713 9.97286 8.81628 10.0942 8.64506C10.2012 8.49408 10.2643 8.31637 10.2763 8.1317C10.2899 7.92227 10.2096 7.70153 10.0491 7.26005L8.88299 4.05321C8.745 3.67376 8.67601 3.48403 8.55442 3.3441C8.44701 3.22049 8.31089 3.12515 8.15802 3.06645C7.98496 3 7.78308 3 7.37932 3H4.56201C4.08188 3 3.84181 3 3.63598 3.09925C3.4655 3.18146 3.29814 3.33701 3.2037 3.50103C3.08968 3.69907 3.07375 3.91662 3.04189 4.35173C3.01413 4.73086 3 5.11378 3 5.5Z" stroke="#F7941D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                <span className="text-sm font-normal ">{contatoData.telefone}</span>
              </a>
            )}

            {contatoData?.endereco && (
              <a href={`tel:${contatoData.endereco}`} className="flex items-center gap-2 text-slate-700 hover:text-blue-900 transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.36328 12.0535C4.01081 11.5723 3.33457 11.3316 3.13309 10.9667C2.95849 10.6504 2.95032 10.2685 3.11124 9.9451C3.29694 9.57185 3.96228 9.30254 5.29295 8.76394L17.8356 3.68716C19.1461 3.15669 19.8014 2.89146 20.2154 3.02745C20.5747 3.14549 20.8565 3.4273 20.9746 3.78662C21.1106 4.20059 20.8453 4.85587 20.3149 6.16643L15.2381 18.709C14.6995 20.0397 14.4302 20.7051 14.0569 20.8908C13.7335 21.0517 13.3516 21.0435 13.0353 20.8689C12.6704 20.6674 12.4297 19.9912 11.9485 18.6387L10.4751 14.4979C10.3815 14.2348 10.3347 14.1032 10.2582 13.9934C10.1905 13.896 10.106 13.8115 10.0086 13.7438C9.89876 13.6673 9.76719 13.6205 9.50407 13.5269L5.36328 12.0535Z" stroke="#F7941D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>


                <span className="text-sm font-normal ">{contatoData.endereco}</span>
              </a>
            )}
          </div>
          <div className='ml-0 lg:ml-6 max-w-full lg:max-w-[700px] bg-white  shadow-lg p-6 lg:mt-16 xl:mt-26 lg:absolute lg:top-1/2 lg:left-1/8 lg:transform lg:-translate-x-1/8 lg:-translate-y-1/2 z-10'>
            {/* Título Principal */}
            {contatoData?.title && (
              <div className="mb-6">
                {(() => {
                  const sentence = contatoData?.title || "";
                  const keywords = contatoData?.PageInfo?.highlight_title || "";
                  const positions = getHighlightPositions(sentence, keywords);
                  return (
                    <HighlightedHeading
                      text={sentence}
                      positions={positions}
                    />
                  );
                })()}
              </div>
            )}

            {contatoData?.description && (
              <div className="mb-6 text-gray-600 text-base font-light" dangerouslySetInnerHTML={{ __html: contatoData.description }} />
            )}


            {/* Mensagens de Feedback */}
            {submitStatus === 'success' && (
              <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Mensagem enviada com sucesso!</span>
                </div>
                <p className="mt-1 text-sm">Entraremos em contato em breve.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-sm">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Erro ao enviar mensagem</span>
                </div>
                <p className="mt-1 text-sm">{errorMessage}</p>
              </div>
            )}

            {/* Formulário */}

            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Nome"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-nunito placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="text"
              name="apelido"
              value={formData.apelido}
              onChange={handleInputChange}
              placeholder="Apelido"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-nunito placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light font-nunito placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <input
              type="tel"
              name="telefone"
              value={formData.telefone}
              onChange={handleInputChange}
              placeholder="Telefone"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light  placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
            />

            <input
              type="text"
              name="assunto"
              value={formData.assunto}
              onChange={handleInputChange}
              placeholder="Assunto"
              className="w-full h-12 px-5 bg-white border-b-1 border-gray-300 text-black text-base font-light  placeholder-gray-500 focus:outline-none focus:border-[#22408B]"
              required
            />

            <textarea
              name="mensagem"
              value={formData.mensagem}
              onChange={handleInputChange}
              placeholder="Mensagem"
              rows={4}
              className="w-full px-5 py-3 bg-white border-b-1 border-gray-300 text-black text-base font-light  placeholder-gray-500 focus:outline-none focus:border-[#22408B] resize-none"
              required
            />

            <div className="flex justify-start mt-6 lg:-mb-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`cursor-pointer rounded-md px-12 h-12 text-white text-lg font-medium  uppercase transition-colors ${isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-[#FEA115] hover:bg-[#FEA115]'
                  }`}
              >
                {isSubmitting ? 'Enviando...' : 'ENVIAR'}
              </button>
            </div>
          </div>
        </form>



        {/* Right Side - Mapa Leaflet (40% em desktop) */}
        <div className="w-full lg:w-4/5 h-96 sm:h-[500px] md:h-[600px] lg:h-screen lg:sticky lg:top-0 overflow-hidden order-2">
          <LeafletMap
            coordenadas={contatoData?.long_latitude}
            localizacao={contatoData?.endereco}
          />
        </div>
      </div>
    </div>
  );
};

export default ContactForm;